import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Button,
    Select,
    Row,
    Col,
    Modal,
    Card,
    Typography,
    Space,
    DatePicker,
    InputNumber,
    AutoComplete,
    Divider,
    Switch,
    message,
    App,
    Spin,
    Table
} from 'antd';
import { EuroOutlined, PercentageOutlined, PlusOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useStyles } from '../theme/hooks';
import {
    createLoan,
    getLoanTypes,
    getBanks,
    getPoolers,
    getReferredBy,
    getReferredByCustomer,
    updateLoan
} from '../services/loanService';
import { searchCustomer, addCustomer } from '../services/customerService';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { UserAddOutlined } from '@ant-design/icons';
import CustomerModal from './CustomerModal/CustomerForm';

const { Option } = Select;

const useLoanFormStyles = () => useStyles((theme) => ({
  form: {
    maxWidth: '2000px',
    margin: '0 auto',
    padding: theme.spacing.lg
  },
  section: {
    marginBottom: theme.spacing.xl,
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.shadows.sm
  },
  sectionTitle: {
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.medium,
    marginBottom: theme.spacing.md,
    color: theme.colors.text.primary
  },
  buttons: {
    marginTop: theme.spacing.xl,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing.md
  }
}));

const LoanForm = ({ mode = 'create', initialValues = null, onSuccess }) => {
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [displayValue, setDisplayValue] = useState('');
    const [loanTypes, setLoanTypes] = useState([]);
    const [banks, setBanks] = useState([]);
    const [filteredBanks, setFilteredBanks] = useState([]);
    const [poolers, setPoolers] = useState([]);
    const [referredBy, setReferredBy] = useState([]);
    const [referredByCustomer, setReferredByCustomer] = useState([]);
    const [isDirectSubmission, setIsDirectSubmission] = useState(true);
    const [isPrognosis, setIsPrognosis] = useState(true);
    const [bankSearchText, setBankSearchText] = useState('');
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const navigate = useNavigate();
    const styles = useLoanFormStyles();
    const [loanParts, setLoanParts] = useState(initialValues?.loan_parts || []);

    useEffect(() => {
        loadFormData();
        // Set initial values
        const defaultValues = {
            reportedAt: dayjs(),
            isDirectSubmission: true,
            isPrognosis: true,
            ...initialValues
        };
        
        if (initialValues?.customer) {
            setSelectedCustomer(initialValues.customer);
            setDisplayValue(`${initialValues.customer.firstName} ${initialValues.customer.lastName}`);
        }
        
        if (initialValues?.reportedAt) {
            defaultValues.reportedAt = dayjs(initialValues.reportedAt);
        }
        
        form.setFieldsValue(defaultValues);
    }, [initialValues]);

    const loadFormData = async () => {
        try {
            setLoading(true);
            const [loanTypesData, banksData, poolersData, referredByData, referredByCustomerData] = await Promise.all([
                getLoanTypes(),
                getBanks(),
                getPoolers(),
                getReferredBy(),
                getReferredByCustomer()
            ]);

            setLoanTypes(loanTypesData);
            setBanks(banksData);
            setFilteredBanks(banksData);
            setPoolers(poolersData);
            setReferredBy(referredByData);
            setReferredByCustomer(referredByCustomerData);
        } catch (error) {
            console.error('Error loading form data:', error);
            message.error(t('loanForm.messages.loadError'));
        } finally {
            setLoading(false);
        }
    };

    const handleCustomerSearch = async (value) => {
        if (!value) {
            setCustomerOptions([]);
            return;
        }

        try {
            const response = await searchCustomer(value);
            const options = response.map(customer => ({
                value: `${customer.name}${customer.first_name ? ` ${customer.first_name}` : ''} - ${customer.city || ''}`,
                label: `${customer.name}${customer.first_name ? ` ${customer.first_name}` : ''} - ${customer.city || ''}`,
                data: customer,
                key: customer.id
            }));
            
            console.log('Formatted customer options:', options);
            setCustomerOptions(options);
        } catch (error) {
            console.error('Error searching customers:', error);
            message.error(t('loanForm.messages.customer.searchError'));
        }
    };

    const handleCustomerSelect = (value, option) => {
        setSelectedCustomer(option?.data || null);
        form.setFieldsValue({ 
            customer: option?.data?.id,
            customerDisplay: value 
        });
    };

    const handleShowCustomerModal = () => {
        setShowCustomerModal(true);
    };

    const handleBankSearch = (value) => {
        setBankSearchText(value);
        const filtered = banks.filter(bank => 
            bank.name.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredBanks(filtered);
    };

    const handleProvisionChange = (value) => {
        // Handle null/undefined case and ensure value is a string
        if (value === null || value === undefined) {
            form.setFieldValue('provision', null);
            return;
        }
        // Convert value to string if it's a number
        const stringValue = value.toString();
        // Convert comma to dot for API format
        const apiValue = stringValue.replace(',', '.');
        form.setFieldValue('provision', apiValue);
    };

    const onFinish = async (values) => {
        try {
            setLoading(true);
            
            // Check if there are any loan parts
            if (loanParts.length === 0) {
                message.error(t('loan.messages.noLoanParts') || 'Bitte füllen Sie mindestens einen Finanzierungsbaustein hinzu');
                setLoading(false);
                return;
            }
            
            // Create base loan data (shared across all loan parts)
            const baseLoanData = {
                customer_id: selectedCustomer?.id,
                bank_id: values.bank_id,
                reported_at: values.reportedAt.format('YYYY-MM-DD'),
                signature_date: values.signatureDate ? values.signatureDate.format('YYYY-MM-DD') : null,
                primary_consultant_id: user.id,
                referred_by_id: values.referredBy,
                referred_by_customer_id: values.referredByCustomer
            };
            
            // Generate a loan_group_id if we have multiple loan parts to link them together
            if (loanParts.length > 1) {
                // Generate a unique ID for this group of loans
                // We can use a timestamp-based ID which is simple and good enough for this purpose
                const loanGroupId = `${selectedCustomer?.id}_${Date.now()}`;
                baseLoanData.loan_group_id = loanGroupId;
            }
            
            // Create an array of promises for creating each loan part
            const savePromises = loanParts.map(part => {
                const loanData = {
                    ...baseLoanData,
                    amount: part.amount,
                    loan_type_id: part.loan_type_id,
                    interest_lock_in_years: part.interest_lock_in_years,
                    provision: part.provision,
                    process_number: part.process_number,
                    extra_bonus: part.extra_bonus
                };
                
                // Only edit mode should use updateLoan
                if (mode === 'edit' && initialValues) {
                    return updateLoan(initialValues.id, loanData);
                } else {
                    return createLoan(loanData);
                }
            });
            
            // Execute all save operations in parallel
            await Promise.all(savePromises);
            
            message.success(mode === 'edit' 
                ? t('loan.messages.updateSuccess') || 'Vorgang erfolgreich aktualisiert'
                : t('loan.messages.createSuccess') || 'Vorgang erfolgreich erstellt');
            
            if (onSuccess) {
                onSuccess();
            } else {
                navigate('/loans');
            }
        } catch (error) {
            console.error('Error:', error);
            
            // Handle validation errors
            if (error && typeof error === 'object') {
                Object.entries(error).forEach(([field, messages]) => {
                    if (Array.isArray(messages)) {
                        messages.forEach(msg => message.error(msg));
                    } else if (typeof messages === 'string') {
                        message.error(messages);
                    }
                });
            } else if (error.message) {
                message.error(error.message);
            } else {
                message.error(t('loan.messages.saveFailed') || 'Fehler beim Speichern des Vorgangs');
            }
        } finally {
            setLoading(false);
        }
    };

    const addLoanPart = () => {
        const values = form.getFieldsValue();
        
        // Validate required fields
        if (!values.amount || !values.loan_type_id) {
            message.error(t('loan.messages.requiredFieldsMissing') || 'Bitte füllen Sie alle Pflichtfelder aus');
            return;
        }
        
        const newPart = {
            amount: values.amount,
            loan_type_id: values.loan_type_id,
            interest_lock_in_years: values.interest_lock_in_years,
            provision: values.provision,
            process_number: values.process_number,
            extra_bonus: values.extra_bonus
        };
        
        setLoanParts([...loanParts, newPart]);
        
        // Clear loan-specific fields
        form.setFieldsValue({
            amount: null,
            loan_type_id: null,
            interest_lock_in_years: null,
            provision: null,
            process_number: null,
            extra_bonus: null
        });
    };
    
    const editLoanPart = (index) => {
        const part = loanParts[index];
        
        // Set form fields with the loan part data to edit
        form.setFieldsValue({
            amount: part.amount,
            loan_type_id: part.loan_type_id,
            interest_lock_in_years: part.interest_lock_in_years,
            provision: part.provision,
            process_number: part.process_number,
            extra_bonus: part.extra_bonus
        });
        
        // Remove the part from the array
        setLoanParts(loanParts.filter((_, i) => i !== index));
    };
    
    const deleteLoanPart = (index) => {
        Modal.confirm({
            title: t('loan.messages.confirmDelete') || 'Finanzierungsbaustein löschen',
            content: t('loan.messages.confirmDeleteContent') || 'Sind Sie sicher, dass Sie diesen Finanzierungsbaustein löschen möchten?',
            okText: t('common.yes') || 'Ja',
            cancelText: t('common.no') || 'Nein',
            onOk: () => {
                setLoanParts(loanParts.filter((_, i) => i !== index));
            }
        });
    };

    if (loading && !loanTypes.length) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spin tip={t('loanForm.messages.loadingOptions')} />
            </div>
        );
    }

    return (<>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={initialValues ? {
                        ...initialValues,
                        reportedAt: initialValues.reported_at ? dayjs(initialValues.reported_at) : undefined,
                        customerDisplay: initialValues.customer ? `${initialValues.customer.first_name} ${initialValues.customer.last_name}` : '',
                        customer: initialValues.customer?.id,
                        referredByCustomer: initialValues.referred_by_customer?.id
                    } : undefined}
                >
                    {/* Customer, Process Number, and Report Date */}
                    <div style={styles.section}>
                        <h3 style={styles.sectionTitle}>{t('loan.fields.customer')}</h3>
                        <Row gutter={24}>
                            <Col xs={24} sm={24} md={12} lg={12}>
                                <div style={{ position: 'relative' }}>
                                    <Button 
                                        type="primary" 
                                        onClick={handleShowCustomerModal} 
                                        icon={<UserAddOutlined />}
                                        style={{ 
                                            position: 'absolute',
                                            right: 0,
                                            top: -10,
                                            zIndex: 1
                                        }}
                                    >
                                        {t('loan.createCustomer')}
                                    </Button>
                                    <Form.Item
                                        name="customerDisplay"
                                        label={t('loan.fields.customer')}
                                        rules={[
                                            { required: true, message: t('common.validation.required') },
                                            {
                                                validator: async (_, value) => {
                                                    if (value && !customerOptions.some(option => option.value === value)) {
                                                        throw new Error(t('common.validation.invalidCustomerOption'));
                                                    }
                                                }
                                            }
                                        ]}
                                    >
                                        <AutoComplete
                                            options={customerOptions}
                                            onSearch={handleCustomerSearch}
                                            onSelect={handleCustomerSelect}
                                            style={{ width: '100%' }}
                                            placeholder={t('loan.search')}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        name="customer"
                                        hidden
                                    >
                                        <Input type="hidden" />
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col xs={24} sm={24} md={12} lg={6}>
                                <Form.Item
                                    name="referredBy"
                                    label={t('loan.fields.referredBy')}
                                >
                                    <Select allowClear>
                                        {referredBy.map(referrer => (
                                            <Option key={referrer.id} value={referrer.id}>
                                                {referrer.name + ', ' + referrer.first_name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={12} lg={6}>
                                <Form.Item
                                    name="referredByCustomer"
                                    label={t('loan.fields.referredByCustomer')}
                                >
                                    <Select allowClear>
                                        {referredByCustomer.map(referrer => (
                                            <Option key={referrer.id} value={referrer.id}>
                                                {referrer.name + ', ' + referrer.first_name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={6} lg={6}>
                                <Form.Item
                                    name="isPrognosis"
                                    label=""
                                    valuePropName="checked"
                                >
                                    <Switch 
                                        checkedChildren={t('loan.fields.prognosis')}
                                        unCheckedChildren={t('loan.fields.prognosis')}
                                        onChange={(checked) => {
                                            setIsPrognosis(checked);
                                            if (checked) {
                                                form.setFieldValue('signatureDate', undefined);
                                            }
                                        }}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="signatureDate"
                                    label={t('loan.fields.signatureDate')}
                                    rules={[{ required: !isPrognosis, message: t('common.validation.required') }]}
                                    hidden={isPrognosis}
                                >
                                    <DatePicker style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                  {/* Bank, Direct Submission, and Pooler */}
                  <div style={styles.section}>
                        <h3 style={styles.sectionTitle}>{t('loan.fields.bank')}</h3>
                        <Row gutter={24}>
                            <Col xs={24} sm={24} md={12} lg={12}>
                                <Form.Item
                                    name="bank_id"
                                    label={t('loan.fields.bank')}
                                    rules={[{ required: false, message: t('common.validation.required') }]}
                                >
                                    <Select
                                        showSearch
                                        placeholder={t('loan.fields.bankSelect')}
                                        optionFilterProp="children"
                                        onSearch={handleBankSearch}
                                    >
                                        {filteredBanks.map(bank => (
                                            <Option key={bank.id} value={bank.id}>
                                                {bank.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>


                    {/* Loan Parts */}
                    {loanParts.length > 0 && (
                        <div style={styles.section}>
                            <h3 style={styles.sectionTitle}>{t('loan.fields.loanParts') || 'Finanzierungsbausteine'}</h3>
                            
                            {loanParts.map((part, index) => (
                                <Card 
                                    key={index} 
                                    style={{ marginBottom: '16px' }}
                                    title={`${t('loan.fields.loanPart')} ${index + 1} - ${loanTypes.find(t => t.id === part.loan_type_id)?.name || ''}`}
                                    extra={
                                        <Space>
                                            <Button type="link" onClick={() => editLoanPart(index)}>
                                                {t('common.edit')}
                                            </Button>
                                            <Button type="link" danger onClick={() => deleteLoanPart(index)}>
                                                {t('common.delete')}
                                            </Button>
                                        </Space>
                                    }
                                >
                                    <Row gutter={24}>
                                        <Col span={8}>
                                            <div>
                                                <strong>{t('loan.fields.amount')}:</strong> {part.amount?.toLocaleString('de-DE')}
                                            </div>
                                        </Col>
                                        <Col span={8}>
                                            <div>
                                                <strong>{t('loan.fields.loanType')}:</strong> {
                                                    loanTypes.find(t => t.id === part.loan_type_id)?.name || ''
                                                }
                                            </div>
                                        </Col>
                                        <Col span={8}>
                                            <div>
                                                <strong>{t('loan.fields.interestLock')}:</strong> {part.interest_lock_in_years || ''}
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row gutter={24} style={{ marginTop: '10px' }}>
                                        <Col span={8}>
                                            <div>
                                                <strong>{t('loan.fields.provision')}:</strong> {part.provision ? Number(part.provision).toLocaleString('de-DE', { minimumFractionDigits: 3, maximumFractionDigits: 8 }) : ''}
                                            </div>
                                        </Col>
                                        <Col span={8}>
                                            <div>
                                                <strong>{t('loan.fields.processNumber')}:</strong> {part.process_number || ''}
                                            </div>
                                        </Col>
                                        <Col span={8}>
                                            <div>
                                                <strong>{t('loan.fields.extraBonus')}:</strong> {part.extra_bonus?.toLocaleString('de-DE') || ''}
                                            </div>
                                        </Col>
                                    </Row>
                                </Card>
                            ))}
                        </div>
                    )}
                    {/* Loan Type, Interest Lock, and Provision */}
                    <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>{t('loan.fields.loan')}</h3>
                    <Row gutter={24}>
                            <Col xs={24} sm={24} md={6} lg={6}>
                                <Form.Item
                                    name="amount"
                                    label={t('loan.fields.amount')}
                                    rules={[{ required: loanParts.length === 0, message: t('common.validation.required') }]}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                                        parser={value => value.replace(/\./g, '')}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={6} lg={6}>
                                <Form.Item
                                    name="loan_type_id"
                                    label={t('loan.fields.loanType')}
                                    rules={[{ required: loanParts.length === 0, message: t('common.validation.required') }]}
                                >
                                    <Select placeholder={t('loan.fields.loanTypeSelect')}>
                                        {loanTypes.map(type => (
                                            <Option key={type.id} value={type.id}>
                                                {type.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={6} lg={6}>
                                <Form.Item
                                    name="interest_lock_in_years"
                                    label={t('loan.fields.interestLock')}
                                    rules={[{ required: false, message: t('common.validation.required') }]}
                                >
                                    <InputNumber style={{ width: '100%' }} min={0} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={6} lg={6}>
                                <Form.Item
                                    name="provision"
                                    label={t('loan.fields.provision')}
                                    rules={[{ required: loanParts.length === 0, message: t('common.validation.required') }]}
                                    getValueFromEvent={(value) => {
                                        if (value === null || value === '') return null;
                                        // Convert the input value to a proper decimal number
                                        return value.toString().replace(',', '.');
                                    }}
                                    normalize={(value) => {
                                        if (value === null || value === '') return null;
                                        // Ensure the value has at most 8 decimal places
                                        const num = parseFloat(value);
                                        return num.toString();
                                    }}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        min={0}
                                        max={9.99999999}
                                        step={0.00000001}
                                        precision={8}
                                        decimalSeparator=","
                                        formatter={value => {
                                            if (value === null || value === '') return '';
                                            // Display with comma as decimal separator
                                            return value.toString().replace('.', ',');
                                        }}
                                        parser={value => {
                                            if (value === null || value === '') return null;
                                            // Parse the input, replacing comma with period
                                            return value.replace(',', '.');
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={6} lg={6}>
                                <Form.Item
                                    name="extra_bonus"
                                    label={t('loan.fields.extraBonus')}
                                    rules={[{ required: false, message: t('common.validation.required') }]}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                                        parser={value => value.replace(/\./g, '')}
                                    />
                                </Form.Item>
                            </Col>   
                            <Col xs={24} sm={24} md={6} lg={6}>
                                <Form.Item
                                    name="process_number"
                                    label={t('loan.fields.processNumber')}
                                    rules={[{ required: false, message: t('common.validation.required') }]}
                                >
                                    <Input />
                                </Form.Item>
                            </Col>  
                            </Row>
                            <Row>
                            <Col xs={24} sm={24} md={12} lg={12}>
                                <div style={styles.buttons}>
                                    <Button type="primary" onClick={addLoanPart}>
                                        {t('loan.addmore')}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>
                    {/* Prognosis and Signature Date */}
                    <div style={styles.section}>
                        <Row gutter={24}>
                            <Col xs={24} sm={24} md={6} lg={6} style={{ display: 'none' }}>
                                <Form.Item
                                    name="reportedAt"
                                    label={t('loan.fields.reportedAt')}
                                    rules={[{ required: true, message: t('common.validation.required') }]}
                                >
                                    <DatePicker style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={24} md={12} lg={12}>
                                <div style={styles.buttons}>
                                    <Button 
                                        type="primary" 
                                        htmlType="submit" 
                                        loading={loading}
                                        disabled={loanParts.length === 0}
                                    >
                                        {t('loan.create')}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Form>

                <CustomerModal
                    visible={showCustomerModal}
                    onCancel={() => setShowCustomerModal(false)}
                    onSuccess={(customer) => {
                        setShowCustomerModal(false);
                        const customerDisplay = `${customer.name}${customer.first_name ? ` ${customer.first_name}` : ''} - ${customer.city || ''}`;
                        const option = {
                            value: customerDisplay,
                            label: customerDisplay,
                            data: customer,
                            key: customer.id
                        };
                        setCustomerOptions([option]);
                        setSelectedCustomer(customer);
                        form.setFieldsValue({ 
                            customer: customer.id,
                            customerDisplay: customerDisplay
                        });
                    }}
                />
                </>
    );
};

export default LoanForm;